import { useRef, useEffect, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Dashboard from '../components/Dashboard/Dashboard';
import Sidebar from '../components/Sidebar/Sidebar';
import Header from '../components/Header';
import {MenuProvider} from '../context/MenuContext';
import { AssistantProvider } from '../context/AssistantContext';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';


// Menu selection is URL-driven: MenuContext reads /dashboard/:section itself.
function MainPageContent() {
    const mainContentRef = useRef(null);
    const sidebarRef = useRef(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const theme = useTheme();
    const location = useLocation();

    // Scroll to top when route changes
    useEffect(() => {
        window.scrollTo(0, 0);
        if (mainContentRef.current) {
            mainContentRef.current.scrollTop = 0;
        }
    }, [location.pathname]);

    // Mobile drawer: any navigation from it (a section, an assistant thread —
    // those only change ?conversation=) closes it and hands focus to the page.
    const navKey = `${location.pathname}${location.search}`;
    const lastNavKey = useRef(navKey);
    useEffect(() => {
        if (lastNavKey.current === navKey) return;
        lastNavKey.current = navKey;
        if (mobileOpen) {
            setMobileOpen(false);
            mainContentRef.current?.focus({ preventScroll: true });
        }
    }, [navKey, mobileOpen]);

    // Escape closes the drawer and returns focus to its toggle.
    useEffect(() => {
        if (!mobileOpen) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') {
                setMobileOpen(false);
                document.querySelector('.navbar-toggle-icon')?.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [mobileOpen]);
    
    const handleMobileToggle = (event) => {
        event.stopPropagation();
        setMobileOpen(!mobileOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.navbar-toggle-icon')) {
                return;
            }
            if (mobileOpen && (!sidebarRef.current || !sidebarRef.current.contains(event.target))) {
                setMobileOpen(false);
            }
        };
        if (mobileOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [mobileOpen]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <CssBaseline />
            <Box
                ref={sidebarRef}
                id="sidebar"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: isMobile ? '4rem' : 0,
                    position: isMobile ? 'fixed' : 'relative',
                    // RTL chrome: sidebar lives on the right; mobile drawer slides
                    // in from the right edge.
                    right: isMobile ? (mobileOpen ? 0 : '-100%') : 0,
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    bgcolor: theme.palette.background.chrome,
                    transition: 'right 0.3s ease, visibility 0.3s',
                    // A closed mobile drawer must not stay in the tab order.
                    visibility: isMobile && !mobileOpen ? 'hidden' : 'visible',
                }} component="nav" aria-label="منوی اصلی">
                <Sidebar mobileOpen={mobileOpen} onMobileToggle={handleMobileToggle} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Header mobileOpen={mobileOpen} onMobileToggle={handleMobileToggle} />
                <div ref={mainContentRef} tabIndex={-1} style={{ display: 'flex', width: '100%', outline: 'none' }}>
                    <Dashboard />
                </div>
            </Box>
        </Box>
    );
}

// Main wrapper component
export default function ClippedDrawer() {
    const { user } = useAuth();
    // Keyed by identity: logging in as someone else (or into another
    // institute) remounts the assistant state, dropping the previous
    // account's threads, drafts and any reply still in flight.
    const identity = user ? `${user.id ?? user.email}:${user.institute_id ?? user.institute_name ?? ''}` : 'anon';
    return (
        <MenuProvider>
            <AssistantProvider key={identity}>
                <MainPageContent />
            </AssistantProvider>
        </MenuProvider>
    );
}
